import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { macroDetailData } from '../../../data/macroData';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

const getTrendIcon = (indicator: string, current: number, forecast: number) => {
  // For indicators where lower is better (inflation, debt, current account deficit)
  const lowerIsBetter = ['Inflation', 'Public Debt', 'Current Account'].includes(indicator);

  if (lowerIsBetter) {
    if (forecast < current) return <TrendingDown size={10} className="text-data-green" />;
    if (forecast > current) return <TrendingUp size={10} className="text-data-red" />;
  } else {
    if (forecast > current) return <TrendingUp size={10} className="text-data-green" />;
    if (forecast < current) return <TrendingDown size={10} className="text-data-red" />;
  }
  return <Minus size={10} className="text-gray-500" />;
};

const getSourceStyle = (source: string): string => {
  switch (source) {
    case 'IMF':
      return 'bg-data-blue/20 text-data-blue';
    case 'WB':
      return 'bg-data-amber/20 text-data-amber';
    case 'CBU':
      return 'bg-data-green/20 text-data-green';
    case 'UN':
      return 'bg-gray-600/30 text-gray-400';
    default:
      return 'bg-gray-600/30 text-gray-400';
  }
};

interface ForecastTableProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const ForecastTable: React.FC<ForecastTableProps> = ({ isMinimized, onMinimize }) => {
  const { forecasts } = macroDetailData;

  return (
    <Panel title="ECONOMIC FORECASTS" subtitle="IMF / WORLD BANK / CBU" status="static" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-auto custom-scrollbar">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-terminal-panel">
              <tr className="text-gray-500 border-b border-terminal-border">
                <th className="text-left py-2 px-2 font-medium">INDICATOR</th>
                <th className="text-center py-2 px-2 font-medium">UNIT</th>
                <th className="text-center py-2 px-2 font-medium">2024</th>
                <th className="text-center py-2 px-2 font-medium text-data-amber">2025E</th>
                <th className="text-center py-2 px-2 font-medium text-data-amber">2026E</th>
                <th className="text-center py-2 px-2 font-medium text-data-amber">2027E</th>
                <th className="text-center py-2 px-2 font-medium text-data-amber">2028E</th>
                <th className="text-center py-2 px-2 font-medium">TREND</th>
                <th className="text-center py-2 px-2 font-medium">SOURCE</th>
              </tr>
            </thead>
            <tbody>
              {forecasts.map((row) => (
                <tr
                  key={row.indicator}
                  className="border-b border-terminal-border/30 hover:bg-data-amber/5 transition-colors"
                >
                  <td className="py-2 px-2 text-gray-300 font-medium">{row.indicator}</td>
                  <td className="py-2 px-2 text-center text-gray-500">{row.unit}</td>
                  <td className="py-2 px-2 text-center text-white font-medium">{row['2024']}</td>
                  <td className="py-2 px-2 text-center text-data-amber">{row['2025E']}</td>
                  <td className="py-2 px-2 text-center text-data-amber">{row['2026E']}</td>
                  <td className="py-2 px-2 text-center text-data-amber">{row['2027E']}</td>
                  <td className="py-2 px-2 text-center text-data-amber">{row['2028E']}</td>
                  <td className="py-2 px-2 text-center">
                    {getTrendIcon(row.indicator, row['2024'], row['2028E'])}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getSourceStyle(row.source)}`}>
                      {row.source}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.IMF} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default ForecastTable;
